// iCloud Key-Value Storage bridge — syncs a curated set of localStorage keys
// across the user's Apple devices via NSUbiquitousKeyValueStore.
//
// Storage model: every value is JSON-stringified before push and JSON-parsed
// on pull. The native side just stores/retrieves strings.
//
// Conflict resolution:
//   - "salahLog" / "fastingLog": OR-merge by date (if either device says
//     Fajr was prayed on 2026-04-25, it stays prayed)
//   - everything else: last-write-wins (NSUbiquitousKeyValueStore handles
//     timestamps internally)
//
// Web/Android paths are no-ops.

export const SYNCED_KEYS = [
  // Worship state — high value to sync
  "salahLog",
  "salahStreak",
  "fastingLog",
  "fastingStreak",
  "tasbeeh",
  "tasbeehSet",
  "azkarCounts",
  "fridaySalawat",
  "readingStreak",
  "duaJournal",
  "muraqabahLog",
  "tajweedStats",
  "tajweedRatings",
  // Paywall state
  "isPro",
  "proExpiry",
  "trialStart",
  "trialExpired",
  // Profile
  "userName",
  "userSurname",
  "userEmail",
  "userWhatsapp",
  "profilePhoto",
  "registeredAt",
  // Followed masjids + radio (so an iCloud restore brings back the user's
  // entire masjid list, custom ordering, and per-masjid azaan toggles)
  "followedMounts",
  "followedNames",
  "followedUrls",
  "notifyMounts",
  "masjidOrder",
  "radioOrder",
  // Notification preferences (the user's intent to receive each notif type;
  // the OS-level permission grant still has to be re-given per device)
  "notifEnabled",
  "dailyNameEnabled",
  "dailyNotifEnabled",
  "prayerReminderEnabled",
  "azaanPrayers",
  // Location / prayer-time config
  "locationMode",
  "detectedCity",
  // Preferences
  "darkMode",
  "selectedReciter",
  "defaultReciterPref",
  "defaultTab",
  "school",
  "surahBookmark",
  "recentSurahs",
  "quranFontSize",
  "surahSpeed",
  "myVerse",
  "liveActivityWindow",
  "shareCardStyle",
  "tabsConfig",
  // Onboarding flags (skip the welcome ceremony etc. on a restored device)
  "ceremonyShown",
  "onboardingDone",
  // Hayah / health — restored on reinstall so no history is lost.
  // calorieLog is trimmed to 90 days before pushing (see flush in App.js)
  // to stay within iCloud KV's 1 MB aggregate limit.
  "hayahPrefs",
  "ghidhaProfile",
  "calorieGoals",
  "caloriesFavorites",
  "calorieWeightLog",
  "calorieFrequency",
  "calorieLog",
  "sirat-workouts",
  "ghidhaMigrationV",
];

const MERGE_BY_DATE_KEYS = new Set(["salahLog", "fastingLog", "calorieLog"]);

function isNative() {
  return !!window.Capacitor?.isNativePlatform?.();
}

function getPlugin() {
  return window.Capacitor?.Plugins?.SiratICloud;
}

export async function isICloudAvailable() {
  if (!isNative()) return false;
  const plugin = getPlugin();
  if (!plugin) return false;
  try {
    const r = await plugin.isAvailable();
    return !!r?.available;
  } catch {
    return false;
  }
}

export async function getICloudDebugSnapshot() {
  const out = {
    native: isNative(),
    pluginPresent: !!getPlugin(),
    available: false,
    values: null,
    error: null
  };
  if (!out.native || !out.pluginPresent) return out;
  try {
    const a = await getPlugin().isAvailable();
    out.available = !!a?.available;
    const all = await getPlugin().getAll();
    out.values = all?.values || {};
  } catch (e) {
    out.error = String(e);
  }
  return out;
}

export async function pushOne(key, rawValue) {
  if (!isNative()) return;
  if (!SYNCED_KEYS.includes(key)) return;
  const plugin = getPlugin();
  if (!plugin) return;
  try {
    if (rawValue === null || rawValue === undefined) {
      await plugin.removeValue({ key });
    } else {
      await plugin.setValue({ key, value: String(rawValue) });
    }
  } catch {}
}

export async function pullAll() {
  if (!isNative()) return null;
  const plugin = getPlugin();
  if (!plugin) return null;
  try {
    const r = await plugin.getAll();
    return r?.values || {};
  } catch {
    return null;
  }
}

export function attachRemoteListener(handler) {
  if (!isNative()) return () => {};
  const plugin = getPlugin();
  if (!plugin?.addListener) return () => {};
  let handle;
  try {
    handle = plugin.addListener("change", (evt) => {
      const keys = evt?.keys || [];
      const values = evt?.values || {};
      const filtered = {};
      keys.forEach((k) => {
        if (!SYNCED_KEYS.includes(k)) return;
        filtered[k] = values[k] ?? null;
      });
      if (Object.keys(filtered).length > 0) handler(filtered);
    });
  } catch {
    return () => {};
  }
  return () => {
    try {
      handle?.remove?.();
      // capacitor 7 returns a Promise from addListener — handle that too
      if (handle?.then) handle.then((h) => { try { h?.remove?.(); } catch {} });
    } catch {}
  };
}

// ── Merge helpers ───────────────────────────────────────────────────────

// salahLog: { "YYYY-MM-DD": { Fajr: bool, Dhuhr: bool, ... } }
// OR-merge per (date, prayer) — once prayed on either device, stays prayed.
function mergeSalahLog(local, remote) {
  if (!remote || typeof remote !== "object") return local || {};
  if (!local || typeof local !== "object") return remote || {};
  const out = { ...local };
  for (const date of Object.keys(remote)) {
    const r = remote[date] || {};
    const l = out[date] || {};
    const day = { ...l };
    for (const p of Object.keys(r)) {
      day[p] = !!l[p] || !!r[p];
    }
    out[date] = day;
  }
  return out;
}

// calorieLog: { "YYYY-MM-DD": { meals: { slot: [entries] }, ... } }
// Per-date merge: prefer whichever side has more food items logged.
// On a fresh install local is empty so remote wins for every date.
function mergeCalorieLog(local, remote) {
  if (!remote || typeof remote !== "object") return local || {};
  if (!local || typeof local !== "object") return remote || {};
  const out = { ...local };
  for (const date of Object.keys(remote)) {
    const r = remote[date];
    const l = out[date];
    if (!l) { out[date] = r; continue; }
    const rItems = Object.values(r.meals || {}).flat().length;
    const lItems = Object.values(l.meals || {}).flat().length;
    if (rItems > lItems) out[date] = r;
  }
  return out;
}

// fastingLog: { "YYYY-MM-DD": "Ramadan" | "Mon" | "Thu" | ... }
// Prefer truthy / longer value if both sides have entries for the same date.
function mergeFastingLog(local, remote) {
  if (!remote || typeof remote !== "object") return local || {};
  if (!local || typeof local !== "object") return remote || {};
  const out = { ...local };
  for (const date of Object.keys(remote)) {
    const r = remote[date];
    const l = out[date];
    if (l == null && r != null) {
      out[date] = r;
    } else if (typeof l === "string" && typeof r === "string" && r.length > l.length) {
      out[date] = r;
    }
  }
  return out;
}

// Returns the merged JSON STRING (or null if nothing changed).
// Inputs are localStorage's raw stringified values; output is the same shape.
export function mergeIfNeeded(key, localStr, remoteStr) {
  if (remoteStr == null) return null;
  if (!MERGE_BY_DATE_KEYS.has(key)) {
    // Last-write-wins: prefer remote unless it equals local already
    return remoteStr === localStr ? null : remoteStr;
  }
  let local, remote;
  try { local = localStr ? JSON.parse(localStr) : {}; } catch { local = {}; }
  try { remote = remoteStr ? JSON.parse(remoteStr) : {}; } catch { remote = {}; }
  const merged = key === "salahLog" ? mergeSalahLog(local, remote)
    : key === "calorieLog" ? mergeCalorieLog(local, remote)
    : mergeFastingLog(local, remote);
  const mergedStr = JSON.stringify(merged);
  return mergedStr === localStr ? null : mergedStr;
}
